import { Images } from 'lucide-react'
import embeddingService, { ImageSearchItem } from '../services/embeddingService'

export const formatCount = (value: number) => value.toLocaleString()
export const formatPercent = (value: number) =>
  `${(value * 100).toFixed(value < 0.01 ? 2 : 1)}%`

/** 검색 결과·클러스터 멤버 공용 썸네일 카드 (우상단에 관련도/유사도 뱃지)
 *
 * compact 는 카드가 작다 — 뱃지도 같이 줄이고 소수점을 버린다. 큰 카드 기준 크기를
 * 그대로 쓰면 작은 썸네일에서 뱃지가 이미지를 거의 다 덮는다.
 *
 * baseline 은 자연어 검색에서만 넘긴다(그 질의의 1위 유사도). 텍스트와 이미지의
 * 코사인 유사도는 절대값이 낮게 나와서 — 잘 맞은 1위가 0.36~0.74 — 그대로 %로 쓰면
 * "46% 일치" 처럼 읽혀 실제 품질을 오해하게 만든다. 1위를 100% 로 놓은 상대값이
 * 검색 필터(관련도)와도 같은 잣대다. 클러스터 멤버는 중심까지의 유사도라 절대값이
 * 그대로 의미를 가지므로 baseline 없이 쓴다.
 */
export default function ImageResultCard({
  item, onOpen, compact = false, baseline,
}: {
  item: ImageSearchItem
  onOpen: (item: ImageSearchItem) => void
  compact?: boolean
  baseline?: number
}) {
  const raw = Math.max(0, item.similarity)
  const relative = baseline && baseline > 0 ? Math.min(1, raw / baseline) : null
  const shown = relative ?? raw
  const badgeTitle = relative != null
    ? `관련도 ${formatPercent(relative)} (유사도 ${raw.toFixed(3)})`
    : `유사도 ${formatPercent(raw)}`
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="group overflow-hidden rounded-lg border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className={`relative bg-slate-100 ${compact ? 'aspect-[4/3]' : 'aspect-square'}`}>
        {item.thumbnail_url ? (
          <img
            src={embeddingService.mediaUrl(item.thumbnail_url)}
            alt={item.filename}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300"><Images size={32} /></div>
        )}
        <span
          className={`absolute rounded bg-gray-900/70 font-semibold text-white ${
            compact ? 'right-1 top-1 px-1 py-0 text-[10px]' : 'right-2 top-2 px-2 py-1 text-[11px]'
          }`}
          title={badgeTitle}
        >
          {compact ? `${Math.round(shown * 100)}%` : formatPercent(shown)}
        </span>
      </div>
      <div className="p-2.5">
        <div className="truncate text-[13px] font-semibold text-slate-700" title={item.filename}>
          {item.filename}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-slate-400" title={item.node_path}>
          {item.node_path}
        </div>
      </div>
    </button>
  )
}
